/**
 * Visual Access Manager (WF-3).
 *
 * Props:
 *   userId           — target employee id
 *   currentUserId    — logged-in admin id (for "copy from" self-guard)
 *   allEmployees     — array of {user_id, employee_name, is_super_admin} to
 *                       power the "Copy Access" combobox
 *   onSaved()        — fired after a successful save
 *
 * Everything else is loaded internally from /api/wf/v3/*.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Save, Copy, Sparkles, EyeOff, Eye, ChevronDown, ChevronRight,
  ShieldCheck, Loader2, LayoutDashboard, Users, Hotel, Calendar, Sparkles as SparkIcon,
  UtensilsCrossed, ChefHat, Wrench, Package, Radar, Banknote, GitBranch,
  FileText, Database, Building2, BellRing, UserRound, Monitor, TrendingUp,
} from "lucide-react";
import { v3API } from "./v3Api";
import { v2API } from "./v2Api";
import { useAuth } from "@/contexts/AuthContext";

const ICON_MAP = {
  LayoutDashboard, Users, Hotel, Calendar, Sparkles: SparkIcon,
  UtensilsCrossed, ChefHat, Wrench, Package, Radar, Banknote, GitBranch,
  FileText, Database, Building2, BellRing, UserRound, Monitor, TrendingUp,
};

const CAP_ROLE_TONE = {
  view:    "bg-slate-100 text-slate-700",
  action:  "bg-sky-100 text-sky-700",
  admin:   "bg-violet-100 text-violet-700",
  danger:  "bg-rose-100 text-rose-700",
  reports: "bg-emerald-100 text-emerald-700",
};

export default function VisualAccessManager({
  userId, currentUserId, allEmployees = [], onSaved,
}) {
  const { user: authUser } = useAuth();
  const selfEdit = String(authUser?._id || authUser?.id || currentUserId || "") === String(userId);
  const [modules, setModules]       = useState([]);
  const [deps, setDeps]             = useState({});
  const [selected, setSelected]     = useState(new Set());
  const [initial, setInitial]       = useState(new Set());
  const [expanded, setExpanded]     = useState(new Set());
  const [query, setQuery]           = useState("");
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [preview, setPreview]       = useState(false);
  const [simulator, setSimulator]   = useState(null);
  const [warnDeps, setWarnDeps]     = useState([]);
  const [copyOpen, setCopyOpen]     = useState(false);
  const [copySearch, setCopySearch] = useState("");

  // ─── Load catalog + current access ──────────────────────────────
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      v3API.modules(),
      v3API.getAccess(userId),
    ]).then(([cat, acc]) => {
      if (cancel) return;
      setModules(cat.modules); setDeps(cat.dependencies);
      const s = new Set(acc.capabilities || []);
      setSelected(s); setInitial(new Set(s));
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Failed to load access manager");
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [userId]);

  // ─── Derived: filtered modules & totals ─────────────────────────
  const filteredModules = useMemo(() => {
    if (!query.trim()) return modules;
    const q = query.toLowerCase();
    return modules
      .map((m) => {
        const caps = m.capabilities.filter(
          (c) => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
                 || m.name.toLowerCase().includes(q),
        );
        return caps.length ? { ...m, capabilities: caps } : null;
      })
      .filter(Boolean);
  }, [modules, query]);

  const enabledModules = useMemo(() => {
    return modules.filter((m) => m.capabilities.some((c) => selected.has(c.key))).length;
  }, [modules, selected]);

  const approvalsCount = useMemo(
    () => Array.from(selected).filter((c) => c.includes("approve")).length, [selected]);
  const reportsCount = useMemo(
    () => Array.from(selected).filter((c) => c.includes("reports.view") || c === "reports.view").length, [selected]);
  const dirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const c of selected) if (!initial.has(c)) return true;
    return false;
  }, [selected, initial]);

  // ─── Actions ─────────────────────────────────────────────────────
  const toggleCap = (key) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(key)) {
        s.delete(key);
      } else {
        s.add(key);
        // Client-side optimistic auto-check of required dependencies so the
        // administrator sees them tick on immediately (matches server behaviour).
        for (const req of deps[key] || []) s.add(req);
      }
      return s;
    });
  };

  const toggleModule = (module) => {
    // If any cap enabled → disable all. Else → enable all "view" + "action" caps
    // (NOT danger by default — admin has to opt-in explicitly).
    const anyEnabled = module.capabilities.some((c) => selected.has(c.key));
    setSelected((prev) => {
      const s = new Set(prev);
      if (anyEnabled) {
        module.capabilities.forEach((c) => s.delete(c.key));
      } else {
        module.capabilities.forEach((c) => {
          if (c.role !== "danger") s.add(c.key);
        });
      }
      return s;
    });
  };

  const copyFromEmployee = async (srcId) => {
    try {
      const r = await v3API.copyFrom(srcId);
      setSelected(new Set(r.capabilities || []));
      setCopyOpen(false);
      toast.success("Access copied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    }
  };

  const openSimulator = async () => {
    try {
      const r = await v3API.simulate(Array.from(selected));
      setSimulator(r);
      setPreview(true);
    } catch (e) {
      toast.error("Simulator failed");
    }
  };

  // Detect dependencies that would auto-add on save
  useEffect(() => {
    if (loading) return;
    const additions = new Set();
    for (const c of selected) {
      const req = deps[c] || [];
      for (const r of req) if (!selected.has(r)) additions.add(r);
    }
    setWarnDeps(Array.from(additions).sort());
  }, [selected, deps, loading]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await v3API.putAccess(userId, {
        capabilities: Array.from(selected),
      });
      setSelected(new Set(r.capabilities || []));
      setInitial(new Set(r.capabilities || []));
      if (r.auto_added?.length) {
        toast.success(`Access saved. Auto-added ${r.auto_added.length} required permission(s).`);
      } else {
        toast.success("Access saved");
      }
      onSaved?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-8 text-center text-slate-400 text-[13px]" data-testid="vam-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2"/> Loading access manager…
      </div>
    );
  }

  if (selfEdit) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-6 text-center space-y-2"
        data-testid="vam-self-edit-blocked">
        <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-amber-700"/>
        </div>
        <div className="text-[14px] font-semibold text-amber-900">You can&apos;t edit your own permissions</div>
        <p className="text-[12.5px] text-amber-800 max-w-md mx-auto leading-relaxed">
          To prevent privilege escalation and accidental self-lockout,
          nobody — not even Super Admins — may modify their own access.
          Ask another admin to make the change on your behalf.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vam-root">
      {/* Copy Access bar (Quick Presets removed — 100% manual toggles) */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5"/> Access Control
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Toggle module cards below to grant access.
            </div>
          </div>
          <button data-testid="vam-copy-btn" onClick={() => setCopyOpen((v) => !v)}
            className="text-[12px] font-medium text-hocp-navy inline-flex items-center gap-1.5 hover:underline">
            <Copy className="w-3.5 h-3.5"/> Copy access from another employee
          </button>
        </div>
        {copyOpen && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <input value={copySearch} onChange={(e) => setCopySearch(e.target.value)}
              placeholder="Search employees…" data-testid="vam-copy-search"
              className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy"/>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-slate-100 bg-white">
              {allEmployees
                .filter((e) => e.user_id !== userId && !e.is_super_admin
                                 && (!copySearch || `${e.employee_name} ${e.email}`.toLowerCase().includes(copySearch.toLowerCase())))
                .slice(0, 30)
                .map((e) => (
                  <button key={e.user_id} onClick={() => copyFromEmployee(e.user_id)} data-testid={`vam-copy-from-${e.user_id}`}
                    className="w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-slate-50 flex items-center justify-between">
                    <span>{e.employee_name}</span>
                    <span className="text-slate-400 text-[11px]">{e.email}</span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary + Search */}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 md:col-span-1 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Access Summary</div>
          <div className="grid grid-cols-3 gap-2">
            <Metric label="Modules" value={enabledModules} testid="vam-summary-modules"/>
            <Metric label="Permissions" value={selected.size} testid="vam-summary-permissions"/>
            <Metric label="Approvals" value={approvalsCount} testid="vam-summary-approvals"/>
          </div>
          {warnDeps.length > 0 && (
            <div className="mt-3 text-[11.5px] p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900" data-testid="vam-dep-warning">
              Enabling these will auto-add {warnDeps.length} required permission(s): <span className="font-mono">{warnDeps.slice(0, 4).join(", ")}{warnDeps.length > 4 ? ` +${warnDeps.length - 4}` : ""}</span>
            </div>
          )}
        </div>
        <div className="col-span-2 md:col-span-1 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Search Permissions</div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="vam-search"
              placeholder="Try 'refund' or 'reservation'"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] outline-none focus:border-hocp-navy focus:bg-white"/>
          </div>
        </div>
      </div>

      {/* Module cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vam-modules">
        {filteredModules.map((m) => {
          const Icon = ICON_MAP[m.icon] || Package;
          const enabledCount = m.capabilities.filter((c) => selected.has(c.key)).length;
          const total = m.capabilities.length;
          const on = enabledCount > 0;
          const isExpanded = expanded.has(m.id) || !!query.trim();
          return (
            <div key={m.id} className={`rounded-2xl border p-3 transition ${
                on ? "border-hocp-navy/30 bg-hocp-navy/[0.02]" : "border-slate-200 bg-white"}`}
              data-testid={`vam-module-${m.id}`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${on ? "bg-hocp-navy text-hocp-gold" : "bg-slate-100 text-slate-500"}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.75}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-hocp-navy">{m.name}</div>
                  <div className="text-[11px] text-slate-500">{m.group} · {enabledCount} / {total} permissions</div>
                </div>
                {/* ON/OFF toggle */}
                <button onClick={() => toggleModule(m)}
                  data-testid={`vam-toggle-${m.id}`}
                  className={`relative w-11 h-6 rounded-full transition ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-5" : "left-0.5"}`}/>
                </button>
                <button onClick={() => setExpanded((e) => {
                    const s = new Set(e);
                    if (s.has(m.id)) s.delete(m.id); else s.add(m.id); return s;
                  })} className="p-1 rounded hover:bg-slate-100"
                  data-testid={`vam-expand-${m.id}`}>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
                </button>
              </div>
              {isExpanded && (
                <div className="mt-3 border-t border-slate-100 pt-2.5 space-y-1.5">
                  {m.capabilities.map((c) => {
                    const checked = selected.has(c.key);
                    const tone = CAP_ROLE_TONE[c.role] || CAP_ROLE_TONE.view;
                    return (
                      <label key={c.key}
                        className={`flex items-start gap-2.5 py-1 px-1 rounded-md cursor-pointer hover:bg-slate-50 ${checked ? "bg-slate-50/70" : ""}`}
                        data-testid={`vam-cap-${c.key}`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => toggleCap(c.key)}
                          className="mt-0.5 accent-emerald-500 w-4 h-4"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-slate-800">{c.label}</div>
                          <div className="text-[10.5px] text-slate-400 font-mono">{c.key}</div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${tone}`}>
                          {c.role}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer actions */}
      <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur border-t border-slate-200 pt-3 pb-2 flex items-center gap-2 justify-end -mx-6 px-6">
        <button onClick={openSimulator} data-testid="vam-preview-btn"
          className="h-9 px-3.5 rounded-lg text-[13px] font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2">
          <Eye className="w-3.5 h-3.5"/> Preview access
        </button>
        <button onClick={() => { setSelected(new Set(initial)); toast.info("Reverted unsaved changes"); }}
          disabled={!dirty} data-testid="vam-revert-btn"
          className="h-9 px-3.5 rounded-lg text-[13px] font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-2">
          Revert
        </button>
        <button onClick={save} disabled={!dirty || saving} data-testid="vam-save-btn"
          className="h-9 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
          {saving ? "Saving…" : "Save Access"}
        </button>
      </div>

      {preview && simulator && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setPreview(false)} data-testid="vam-simulator-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
                <Eye className="w-4 h-4 text-sky-700"/>
              </div>
              <div className="flex-1">
                <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Role Simulator</h3>
                <p className="text-[12px] text-slate-500">
                  {simulator.modules_enabled} module(s) visible · {simulator.capability_count} capability(ies)
                </p>
              </div>
              <button onClick={() => setPreview(false)} className="p-1.5 rounded hover:bg-slate-100">
                <EyeOff className="w-4 h-4 text-slate-500"/>
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {simulator.modules.map((m) => (
                <div key={m.id} className={`rounded-lg border p-2.5 ${m.enabled ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 opacity-50"}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[13px] font-medium text-hocp-navy">{m.name}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${m.enabled ? "bg-emerald-500 text-white" : "bg-slate-300 text-white"}`}>
                      {m.enabled ? `${m.granted.length}/${m.total} granted` : "Hidden"}
                    </span>
                  </div>
                  {m.enabled && m.granted.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {m.granted.join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 py-2 px-2 text-center" data-testid={testid}>
      <div className="text-[20px] font-semibold text-hocp-navy leading-none">{value}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mt-1 font-semibold">{label}</div>
    </div>
  );
}
